import { Autocomplete, Box, Button, Collapse, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { skipToken } from '@reduxjs/toolkit/query';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import ActionBarDateTimePicker from '../../../shared/ui/ActionBarDateTimePicker/ActionBarDateTimePicker';
import style from './TariffPlansFiltersWidget.module.css';
import useAuth from '../../../shared/lib/hooks/useAuth';
import { useGetCompaniesQuery } from '../../../entities/companies/companyAccounts/redux/companyAccounts.api';

const initialFilters = {
  name: '',
  startDate: null,
  startDateUntil: null,
  finishDate: null,
  finishDateUntil: null,
  dealerCompany: null,
};

// Отображает поля фильтрации и кнопки применения/сброса фильтров
function TariffPlansFiltersWidget({ isOpenFilters, applyFilters }) {
  const { isDealer } = useAuth();

  const [filters, setFilters] = useState({ ...initialFilters });
  // Изменение в поле ввода даты
  const handleChangeDate = (dateField, newValue) => {
    setFilters((prev) => ({ ...prev, [dateField]: newValue }));
  };
  // Сброс полей фильтров
  const handleResetFilters = () => {
    setFilters({ ...initialFilters });
  };

  const { data: companies } = useGetCompaniesQuery(isDealer ? skipToken : {});
  const dealerCompanies = useMemo(() => {
    if (!companies) return [];

    return companies?.filter((company) => !!company.isDealer);
  }, [companies]);

  return (
    <Collapse in={isOpenFilters}>
      <Box className={style.tariffPlansFiltersWidget}>
        <Box className={style.filters}>
          <TextField
            size="small"
            id="name"
            label="Название"
            value={filters.name}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, name: e.target.value }));
            }}
          />
          {!isDealer && (
            <Autocomplete
              sx={{ width: '240px' }}
              size="small"
              autoComplete
              value={filters?.dealerCompany}
              onChange={(_, newValue) => {
                setFilters((prev) => ({ ...prev, dealerCompany: newValue }));
              }}
              id="route"
              options={dealerCompanies}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
              renderInput={(params) => <TextField {...params} label="Дилер" />}
            />
          )}
          <Box className={style.dates}>
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
                Дата начала
              </Typography>
              <Box className={style.dateTimePeriod}>
                <DatePicker
                  label="C"
                  value={filters.startDate}
                  maxDate={filters.startDateUntil}
                  onChange={(newDate) => {
                    handleChangeDate('startDate', newDate);
                  }}
                  renderInput={(params) => <TextField variant="standard" size="small" {...params} />}
                  components={{
                    ActionBar: ActionBarDateTimePicker,
                  }}
                />
                <DatePicker
                  label="По"
                  value={filters.startDateUntil}
                  minDate={filters.startDate}
                  onChange={(newDate) => handleChangeDate('startDateUntil', newDate)}
                  renderInput={(params) => <TextField variant="standard" size="small" {...params} />}
                  components={{
                    ActionBar: ActionBarDateTimePicker,
                  }}
                />
              </Box>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
                Дата окончания
              </Typography>
              <Box className={style.dateTimePeriod}>
                <DatePicker
                  label="C"
                  maxDate={filters.finishDateUntil}
                  value={filters.finishDate}
                  onChange={(newDate) => {
                    handleChangeDate('finishDate', newDate);
                  }}
                  renderInput={(params) => <TextField variant="standard" size="small" fullWidth {...params} />}
                  components={{
                    ActionBar: ActionBarDateTimePicker,
                  }}
                />
                <DatePicker
                  label="По"
                  value={filters.finishDateUntil}
                  minDate={filters.finishDate}
                  onChange={(newDate) => handleChangeDate('finishDateUntil', newDate)}
                  renderInput={(params) => <TextField variant="standard" size="small" fullWidth {...params} />}
                  components={{
                    ActionBar: ActionBarDateTimePicker,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className={style.filterButtons}>
          <Button disableElevation onClick={handleResetFilters} variant="text">
            Сбросить
          </Button>
          <Button disableElevation onClick={() => applyFilters(filters)} variant="contained">
            Поиск
          </Button>
        </Box>
      </Box>
    </Collapse>
  );
}

TariffPlansFiltersWidget.propTypes = {
  isOpenFilters: PropTypes.bool.isRequired,
  applyFilters: PropTypes.func.isRequired,
};

export default TariffPlansFiltersWidget;
